/* eslint-disable react/jsx-props-no-spreading */
import { ArrowBackIcon } from '@chakra-ui/icons';
import { Alert, AlertIcon, AlertTitle, Box, Button, Icon, Spinner } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import RHForm from '../../Components/RHForm/RHForm';
import {
  CAICarousel,
  CAnthropicAI,
  CArticleShowcase,
  CCasesShowcase,
  CContact,
  CEvents,
  CFAQ,
  CImage,
  CIntroduction,
  CMarkdown,
  COpenAI,
  CPageBanner,
  CPartnersSlider,
  CPeopleShowcase,
  CPrograms,
  CPromo,
  CQuoteSlider,
  CResult,
  CSimpleSlider,
  CSupport,
  CTextImage,
  CTitle,
  CUSP,
  CVertexAI,
} from '../../core/components';
import { useUserContext } from '../../core/context/user.context';
import useFetch from '../../core/hooks/useFetch';

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL;
const COMPONENTS = [
  CContact,
  CTitle,
  CResult,
  CPeopleShowcase,
  CIntroduction,
  COpenAI,
  CAnthropicAI,
  CSimpleSlider,
  CPrograms,
  CCasesShowcase,
  CArticleShowcase,
  CPartnersSlider,
  CQuoteSlider,
  CPromo,
  CImage,
  CFAQ,
  CAICarousel,
  CEvents,
  CSupport,
  CVertexAI,
  CTextImage,
  CMarkdown,
  CUSP,
];

const SIMPLE_FORM = {
  endpoint: 'page',
  itemPath: 'page',
  backPath: '/website?tab=4',
  withPublish: true,
  schema: [
    {
      name: 'world_page',
      type: 'boolean',
      label: '',
      hidden: true,
      defaultValue: false,
    },
    {
      name: 'title',
      type: 'string',
      label: 'Title',
      required: true,
    },
    {
      name: 'slug',
      type: 'string',
      label: 'Slug',
      required: true,
    },
    {
      name: 'parent',
      type: 'ref',
      entity: 'page',
      label: 'Parent page',
    },
    {
      name: 'body',
      type: 'object',
      label: '',
      schema: [
        {
          name: 'seo',
          type: 'object',
          label: 'SEO',
          schema: [
            {
              name: 'title',
              type: 'string',
              label: 'Title',
            },
            {
              name: 'description',
              type: 'textarea',
              label: 'Description',
            },
            {
              name: 'canonical',
              type: 'string',
              label: 'Canonical',
            },
            {
              name: 'noIndex',
              type: 'boolean',
              label: 'No Index',
            },
            {
              name: 'image_id',
              type: 'img',
              label: 'Image',
            },
          ],
        },
      ],
    },
  ],
};
const FULL_FORM = {
  endpoint: 'page',
  itemPath: 'page',
  backPath: '/website?tab=4',
  withPublish: true,
  withDuplicate: true,
  schema: [
    {
      name: 'world_page',
      type: 'boolean',
      label: '',
      hidden: true,
      defaultValue: false,
    },
    {
      name: 'title',
      type: 'string',
      label: 'Title',
      required: true,
    },
    {
      name: 'slug',
      type: 'string',
      label: 'Slug',
      required: true,
    },
    {
      name: 'parent',
      type: 'ref',
      entity: 'page',
      label: 'Parent page',
    },
    {
      name: 'type',
      type: 'select',
      label: 'Type',
      options: [
        {
          label: 'Default',
          value: 'default',
        },
        {
          label: 'Simple',
          value: 'simple',
        },
      ],
      defaultValue: 'default',
      changing: ['body'],
    },
    {
      name: 'password',
      type: 'string',
      label: 'Password',
    },
    {
      name: 'alternativeNav',
      type: 'array',
      label: 'Alternative Navigation',
      schema: [
        {
          name: 'hasSubMenu',
          type: 'boolean',
          label: 'Has sub-menu',
          changing: ['href', 'subItems'],
        },
        {
          name: 'label',
          type: 'string',
          label: 'Label',
          required: true,
        },
        {
          name: 'href',
          type: 'string',
          label: 'Target',
        },
        {
          name: 'subItems',
          type: 'array',
          label: 'Sub items',
          condition: ['hasSubMenu'],
          conditionValues: [true],
          schema: [
            {
              name: 'label',
              type: 'string',
              label: 'Label',
              required: true,
            },
            {
              name: 'href',
              type: 'string',
              label: 'Target',
              required: true,
            },
          ],
        },
      ],
    },
    {
      name: 'hideNav',
      type: 'boolean',
      label: 'Hide Navigation',
    },
    {
      name: 'hideFooter',
      type: 'boolean',
      label: 'Hide Footer',
    },
    {
      name: 'disableHomeLink',
      type: 'boolean',
      label: 'Disable home link',
    },
    {
      name: 'body',
      type: 'object',
      label: '',
      condition: ['type'],
      conditionValues: ['default'],
      schema: [
        CPageBanner,
        {
          name: 'pageBuilder',
          type: 'component',
          label: 'Page Builder',
          components: COMPONENTS,
        },
        {
          name: 'seo',
          type: 'object',
          label: 'SEO',
          schema: [
            {
              name: 'title',
              type: 'string',
              label: 'Title',
            },
            {
              name: 'description',
              type: 'textarea',
              label: 'Description',
            },
            {
              name: 'canonical',
              type: 'string',
              label: 'Canonical',
            },
            {
              name: 'noIndex',
              type: 'boolean',
              label: 'No Index',
            },
            {
              name: 'image_id',
              type: 'img',
              label: 'Image',
            },
          ],
        },
      ],
    },
    {
      name: 'body',
      type: 'object',
      label: '',
      condition: ['type'],
      conditionValues: ['simple'],
      schema: [
        {
          name: 'pageBuilder',
          type: 'component',
          label: 'Page Builder',
          components: COMPONENTS,
        },
        {
          name: 'seo',
          type: 'object',
          label: 'SEO',
          schema: [
            {
              name: 'title',
              type: 'string',
              label: 'Title',
            },
            {
              name: 'description',
              type: 'textarea',
              label: 'Description',
            },
            {
              name: 'canonical',
              type: 'string',
              label: 'Canonical',
            },
            {
              name: 'noIndex',
              type: 'boolean',
              label: 'No Index',
            },
            {
              name: 'image_id',
              type: 'img',
              label: 'Image',
            },
          ],
        },
      ],
    },
  ],
};
const PAGE_URL = `${process.env.REACT_APP_API_URL}/page`;
const SIMPLE_PAGE_ARRAY = [
  'home-page',
  'about-us',
  'community',
  'blog',
  'general-data',
  'world-general-data',
  'world-home-page',
];

function Page() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useUserContext();
  const { result, doFetch, loading, error } = useFetch(`${PAGE_URL}/${id}`);
  const page = useMemo(() => result?.data, [result]);

  if (page) {
    page.world_page = false;
  }

  useEffect(() => {
    if (id === 'new') return;
    doFetch({
      headers: {
        Authorization: `Bearer ${currentUser.token}`,
      },
    });
  }, [id]);

  const form = useMemo(
    () =>
      !result?.data?.slug || SIMPLE_PAGE_ARRAY.includes(result.data.slug) ? SIMPLE_FORM : FULL_FORM,
    [result?.data?.slug],
  );

  return (
    <section>
      <Button variant="link" onClick={() => navigate(-1)} leftIcon={<ArrowBackIcon />} p={5}>
        Back
      </Button>
      <Box p={5}>
        {loading ? (
          <Spinner />
        ) : error ? (
          <Alert status="error" my={10}>
            <AlertIcon />
            <AlertTitle>Could not load the page with id: {id}</AlertTitle>
          </Alert>
        ) : !page ? (
          <>
            <h1 className="h3">Create new page</h1>
            <Box bg="white" my={10} p={5} borderRadius={6} boxShadow="lg" flex="1 1 auto">
              <RHForm {...SIMPLE_FORM} />
            </Box>
          </>
        ) : (
          <>
            <h1 className="h3">{page.title}</h1>
            <div className="h4">
              <a
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '8px',
                  lineHeight: 1,
                }}
                href={`${WEBSITE_URL}${page.parent ? `/${page.parent}` : ''}/${page.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Visit page"
              >
                {page.slug}
                <Icon
                  as={FiExternalLink}
                  style={{ width: '1.1rem', height: '1.1rem', display: 'block' }}
                />
              </a>
            </div>
            <Box bg="white" my={10} p={5} borderRadius={6} boxShadow="lg" flex="1 1 auto">
              <RHForm itemValues={page} id={page._id} fetchItem={doFetch} withPreview {...form} />
            </Box>
          </>
        )}
      </Box>
    </section>
  );
}

export default Page;
